import * as forms from "@client/forms";
import {Label} from "@client/forms";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {Register} from "@client/site/components";
import {useStaticURL} from "@client/site/context";
import {Layout} from "@client/site/Layout";
import * as style from "@client/style";
import {server} from "@reactivated";
import {CardElement, Elements, useElements, useStripe} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js/pure";
import React from "react";

const styles = {
    spacer: "space-y-[15px] flex flex-col",
    cardElement:
        "h-[35px] px-2.5 py-[5px] block text-sm shrink basis-full bg-white rounded-md font-light outline-none appearance-none border border-[#D8D8D8] flex flex-col justify-center",
};

export const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: style.colors.text,
            fontFamily: "InterVariable",
            fontSmoothing: "antialiased",
            fontWeight: "300",
            fontSize: "14px",
            "::placeholder": {
                color: "#999999",
            },
        },
        invalid: {
            color: style.commonColors.errorFontColor,
            iconColor: style.commonColors.errorBorderColor,
        },
    },
};

const ManagerRegistration = (props: server.manager.templates.ManagerRegistration) => {
    // Stripe mints the token on submit, after the form is ready, so it is not
    // a field the form waits on.
    const {token: _token, ...fields} = props.form.fields;
    const form = forms.useForm({...props.form, fields});
    const stripe = useStripe();
    const elements = useElements();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [distinctId, setDistinctId] = React.useState("");
    const [token, setToken] = React.useState(
        forms.boundText(props.form.drafts?.token, props.form.defaults.token) ?? "",
    );
    const [stripeError, setStripeError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setDistinctId(analytics.getDistinctId());
    }, []);

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        if (!forms.ready(form)) {
            return;
        }
        event.persist();
        const target = event.target as HTMLFormElement;

        if (token === "") {
            const card = elements?.getElement(CardElement);
            if (stripe == null || card == null) {
                setStripeError("Stripe could not load correctly");
                return;
            }
            const {error, paymentMethod} = await stripe.createPaymentMethod({
                type: "card",
                card,
            });
            if (paymentMethod != null) {
                setToken(paymentMethod.id);
                setIsSubmitting(true);
                // Needs to be in the next tick.
                setTimeout(() => target.submit(), 1);
            } else {
                setStripeError(error.message ?? null);
            }
        } else {
            setIsSubmitting(true);
            target.submit();
        }
    };

    const title = "Get started with Joy for Business";
    const markup = (
        <>
            Get started with Joy for <em>Business</em>
        </>
    );

    return (
        <Layout isBusiness narrow title={title}>
            <form
                onSubmit={handleSubmit}
                noValidate
                method="POST"
                action=""
                className={styles.spacer}
            >
                <forms.CSRFToken />
                <forms.FormErrors form={form} />
                <typography.Header>{markup}</typography.Header>
                <typography.P>Please fill out the details below.</typography.P>
                <forms.Fields
                    form={form}
                    exclude={["distinct_id", "with_notification"]}
                />

                <input type="hidden" name="distinct_id" defaultValue={distinctId} />
                <input type="hidden" name="token" defaultValue={token} />

                {token === "" && (
                    <Label label="Payment details" error={stripeError}>
                        <CardElement
                            className={styles.cardElement}
                            options={CARD_ELEMENT_OPTIONS}
                        />
                    </Label>
                )}

                <typography.P>
                    You will <strong>not</strong> be charged. Your first client is free.
                </typography.P>
                <Register isSubmitting={isSubmitting} />
            </form>
        </Layout>
    );
};

export const Template = (props: server.manager.templates.ManagerRegistration) => {
    const staticURL = useStaticURL();
    const fonts = [
        {
            family: "InterVariable",
            src: `url('${staticURL}fonts/InterVariable.woff2')`,
            weight: "300",
        },
    ];

    const [stripe, setStripe] = React.useState<ReturnType<typeof loadStripe> | null>(
        null,
    );

    React.useEffect(() => {
        setStripe(loadStripe(props.STRIPE_PUBLISHABLE_KEY));
    }, []);

    return (
        <Elements stripe={stripe} options={{fonts}}>
            <ManagerRegistration {...props} />
        </Elements>
    );
};
